<template>
  <v-dialog v-model="dialog" max-width="500">
    <template v-slot:activator="{ on }">
      <v-btn color="primary" fab title="Create Fund Bucket Item" v-on="on">
        <v-icon medium> mdi-plus-circle </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      :loading="loading"
      color="primary"
      style="margin-bottom: 0"
    >
      <template v-slot:header>
        <v-card-title>
          <div class="px-2">
            <h4 class="title font-weight-light">Fund Bucket Item</h4>
            <p class="category font-weight-thin">Create Fund Bucket Item</p>
          </div>
          <v-spacer />
          <v-btn text icon color="white" @click="dialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
      </template>
      <v-form v-if="dialog && !loading" ref="form" lazy-validation>
        <v-container>
          <v-row>
            <v-col>
              <v-combobox
                v-model="newBucketItem.schema"
                item-text="name"
                :items="schemas"
                :rules="schemaRules"
                label="Schema"
                required
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-combobox
                v-model="newBucketItem.company"
                item-text="name"
                item-value="id"
                :items="companies"
                :rules="companyRules"
                label="Company"
                required
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-combobox
                v-model="newBucketItem.bucket"
                item-text="name"
                item-value="id"
                :items="filteredBucketTypes"
                :rules="bucketRules"
                label="Bucket"
                required
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-combobox
                v-model="newBucketItem.description"
                item-text="description"
                item-value="id"
                :items="bucketDescriptions"
                :rules="descriptionRules"
                label="Description"
                required
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-text-field
                v-model="newBucketItem.min"
                label="Min. Amount"
                :rules="[minLogic]"
                required
                @input="
                  $set(newBucketItem, newBucketItem.min, parseFloat($event))
                "
              />
            </v-col>
            <v-col>
              <v-text-field
                v-model="newBucketItem.interval"
                label="Interval"
                :rules="[intervalLogic]"
                required
                @input="
                  $set(
                    newBucketItem,
                    newBucketItem.interval,
                    parseFloat($event)
                  )
                "
              />
            </v-col>
            <v-col>
              <v-text-field
                v-model="newBucketItem.max"
                label="Max. Amount"
                :rules="[maxLogic]"
                required
                @input="
                  $set(newBucketItem, newBucketItem.max, parseFloat($event))
                "
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="3">
              <p class="mt-5">Has Quota?</p>
            </v-col>
            <v-col cols="9">
              <v-switch v-model="newBucketItem.quota" color="success" />
            </v-col>
          </v-row>
          <v-card-actions>
            <v-spacer />
            <v-btn :loading="progressing" color="success" @click="submitForm">
              Create
            </v-btn>
          </v-card-actions>
        </v-container>
      </v-form>
    </material-card>
  </v-dialog>
</template>

<script>
export default {
  data() {
    return {
      dialog: false,
      loading: false,
      schemaRules: [(v) => !!v || "*This field is required"],
      companyRules: [(v) => !!v || "*This field is required"],
      bucketRules: [(v) => !!v || "*This field is required"],
      descriptionRules: [(v) => !!v || "*This field is required"],
      minRules: [
        (v) => !!v || "*This field is required",
        (v) => (!!v && !isNaN(v)) || "Only number is allowed",
        (v) =>
          (!!v && !isNaN(v) && /^\d+(\.\d{1,2})?$/.test(v)) ||
          "*Only 2 Decimal is allowed",
      ],
      maxRules: [
        (v) => !!v || "*This field is required",
        (v) => (!!v && !isNaN(v)) || "Only number is allowed",
        (v) =>
          (!!v && !isNaN(v) && /^\d+(\.\d{1,2})?$/.test(v)) ||
          "*Only 2 Decimal is allowed",
      ],
      intervalRules: [
        (v) => !!v || "*This field is required",
        (v) => (!!v && !isNaN(v)) || "Only number is allowed",
      ],

      newBucketItem: {
        schema: "",
        company: "",
        bucket: "",
        description: "",
        min: "",
        max: "",
        interval: "",
        quota: false,
      },

      companies: [],
      fundBucketHasTypes: [],
      bucketDescriptions: [],
      schemas: [],
      progressing: false,
    };
  },
  computed: {
    filteredBucketTypes() {
      if (!this.newBucketItem.schema) return;

      return this.fundBucketHasTypes.filter((fundBucketType) => {
        return fundBucketType.schemaName === this.newBucketItem.schema;
      });
    },
  },
  watch: {
    dialog(value) {
      if (!value) {
        this.$resetData();
        this.$refs.form.resetValidation();
      } else {
        this.loadData();
      }
    },
    newBucketItem: "validate",
  },
  methods: {
    loadData() {
      this.loading = true;
      Promise.all([
        this.$api.companies().list(),
        this.$api.fundBucketItemDescriptions().list(),
        this.$api.companies().withSchema(),
        this.$api.fundBucketHasTypes().list(),
      ]).then(
        ([companies, bucketDescriptions, schemas, fundBucketHasTypes]) => {
          this.companies = companies.data;
          this.bucketDescriptions = bucketDescriptions.data;
          schemas.data.map((item) => {
            this.schemas.push(item.schema_name);
          });
          fundBucketHasTypes.data.map((item) => {
            const itemCategory = String(item.category)
              .split("_")
              .join("-")
              .toUpperCase();

            this.fundBucketHasTypes.push({
              id: item.fund_bucket.id,
              name: `${item.fund_bucket.name} - ${itemCategory}`,
              schemaName: item.schema_name,
            });
            this.loading = false;
          });
        }
      );
    },
    validate() {
      this.$refs.form.validate();
    },
    maxLogic() {
      if (this.newBucketItem.max !== "") {
        if (!isNaN(this.newBucketItem.max)) {
          if (/^\d+(\.\d{1,2})?$/.test(this.newBucketItem.max)) {
            if (
              parseFloat(this.newBucketItem.max) <=
              parseFloat(this.newBucketItem.min)
            ) {
              return "Max cannot less than or equal to Min";
            }

            if (
              this.newBucketItem.min !== "" &&
              this.newBucketItem.interval !== ""
            ) {
              const min = parseFloat(this.newBucketItem.min);
              const interval = parseFloat(this.newBucketItem.interval);
              const max = parseFloat(this.newBucketItem.max);
              let n = 0;

              if (
                parseFloat(this.newBucketItem.interval) >
                parseFloat(this.newBucketItem.max)
              ) {
                n = Math.ceil((max - min) / interval);
              } else {
                n = Math.floor((max - min) / interval);
              }

              let actualMax = n * interval + min;

              while (!/^\d+(\.\d{1,2})?$/.test(actualMax) && n > 0) {
                const a = n * interval + min;
                actualMax = a;
                n--;
              }

              if (
                (((parseFloat(this.newBucketItem.max) -
                  parseFloat(this.newBucketItem.min)) *
                  100) %
                  (parseFloat(this.newBucketItem.interval) * 100)) /
                  100 !==
                0
              ) {
                return "Do you mean " + actualMax + "?";
              }
            }
          } else {
            return "*Only 2 Decimal is allowed";
          }
        } else {
          return "Only number is allowed";
        }
      } else {
        return "*This field is required";
      }

      return true;
    },
    minLogic() {
      if (this.newBucketItem.min !== "") {
        if (!isNaN(this.newBucketItem.min)) {
          if (/^\d+(\.\d{1,2})?$/.test(this.newBucketItem.min)) {
            if (
              parseFloat(this.newBucketItem.min) >=
              parseFloat(this.newBucketItem.max)
            ) {
              return "Min cannot greater than or equal to Max";
            }
            if (
              this.newBucketItem.interval !== "" &&
              this.newBucketItem.max !== ""
            ) {
              const min = parseFloat(this.newBucketItem.min);
              const interval = parseFloat(this.newBucketItem.interval);
              const max = parseFloat(this.newBucketItem.max);

              let n = Math.floor((max - min) / interval);

              let actualMin = max - n * interval;

              while (!/^\d+(\.\d{1,2})?$/.test(actualMin) && n > 0) {
                const a = max - n * interval;
                actualMin = a;
                n--;
              }

              if (
                (((parseFloat(this.newBucketItem.max) -
                  parseFloat(this.newBucketItem.min)) *
                  100) %
                  (parseFloat(this.newBucketItem.interval) * 100)) /
                  100 !==
                0
              ) {
                return "Do you mean " + actualMin + "?";
              }
            }
          } else {
            return "*Only 2 Decimal is allowed";
          }
        } else {
          return "Only number is allowed";
        }
      } else {
        return "*This field is required";
      }

      return true;
    },
    intervalLogic() {
      if (this.newBucketItem.interval !== "") {
        if (!isNaN(this.newBucketItem.interval)) {
          if (/^\d+(\.\d{1,2})?$/.test(this.newBucketItem.interval)) {
            if (
              parseFloat(this.newBucketItem.interval) >
              parseFloat(this.newBucketItem.max)
            ) {
              return "Interval cannot greater than Max";
            }

            if (
              this.newBucketItem.min !== "" &&
              this.newBucketItem.max !== ""
            ) {
              const min = parseFloat(this.newBucketItem.min);
              const interval = parseFloat(this.newBucketItem.interval);
              const max = parseFloat(this.newBucketItem.max);

              let n = Math.floor((max - min) / interval);

              let actualInterval = (max - min) / n;

              while (!/^\d+(\.\d{1,2})?$/.test(actualInterval) && n > 0) {
                const a = (max - min) / n;
                actualInterval = a;
                n--;
              }

              if (
                (((parseFloat(this.newBucketItem.max) -
                  parseFloat(this.newBucketItem.min)) *
                  100) %
                  (parseFloat(this.newBucketItem.interval) * 100)) /
                  100 !==
                0
              ) {
                return "Do you mean " + actualInterval + "?";
              }
            }
          } else {
            return "*Only 2 Decimal is allowed";
          }
        } else {
          return "Only number is allowed";
        }
      } else {
        return "*This field is required";
      }

      return true;
    },
    submitForm() {
      if (this.$refs.form.validate()) {
        this.$api
          .fundBucketItems()
          .create(this.prepareFormData())
          .then((response) => {
            if (response.status === 201) {
              this.$emit("created");
              this.$snack({ color: "success", message: response.data.message });

              this.$refs.form.resetValidation();
              this.dialog = false;
            }
          })
          .catch((err) => {
            this.$snack({ color: "error", message: err.response.statusText });
          })
          .then(() => {
            this.progressing = false;
          });
      }
    },
    prepareFormData() {
      return {
        company_id: this.newBucketItem.company.id,
        bucket_id: this.newBucketItem.bucket.id,
        description_id: this.newBucketItem.description.id,
        min: this.newBucketItem.min,
        max: this.newBucketItem.max,
        multiple_of: this.newBucketItem.interval,
        has_quota: this.newBucketItem.quota ? "1" : "0",
        priority: 1,
      };
    },
  },
};
</script>
